import React, { Suspense, useCallback } from 'react';
import { clsx } from '@core/js/utils/classNames.util';
import { pick, prop } from 'ramda';
import * as urlUtil from '@core/js/utils/url.util';
import { dispatcher } from 'dispatcher';
import { Button } from '@theme/js/components/common/Button';
import { TotalizatorSessionTicker } from '@theme/js/components/main/TotalizatorSessionTicker';
import { useAppSelector, usePageSelector, useUserSelector } from '@core/js/store/hooks';
import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';
import { useComponentTranslations } from '@core/js/hooks/component/useComponentTranslations';

import { Logo } from '../Logo';
import { Inbox } from '../Inbox';
import { Icon } from '@core/js/components/common/Icon';
import * as hooks from '@core/js/components/main/DefaultHeader/hooks';
import { MyAccount } from '../MyAccount';
import { MyBalance } from '../MyBalance';
import { DropdownAccount } from '../DropdownAccount';

import style from './TabletHeader.scss';

export const TabletHeader: React.FC = () => {
    const translate = useComponentTranslations();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const hideChatByUrl = urlUtil.getLocationParam('hideChat') === '1';
    const { registrationURL, chatWidget } = useAppSelector(pick(['registrationURL', 'chatWidget']));
    const { overrideMenu, menuWcId } = usePageSelector(pick(['overrideMenu', 'menuWcId']));
    const hideSideMenuIcon = overrideMenu && !menuWcId;
    const { isShowBackButton, handleBackButtonClick } = hooks.useBackButton();
    const openSideMenu = useCallback(() => dispatcher.dispatch('menu:action:open'), []);
    const onLoginButtonClick = hooks.useOnLoginButtonClick();
    const enableChat = chatWidget.enabled && chatWidget.showIcon && !hideChatByUrl;

    const Chat = enableChat ? (
        <div className={style.chat}>
            <BackboneReplacer dynamicName="CHAT_BUTTON" viewName="chatbutton" />
        </div>
    ) : null;

    return (
        <div className={style.header}>
            <div className={style.info}>
                <span className={style.burgerContainer}>
                    {!hideSideMenuIcon && !isShowBackButton && (
                        <Icon
                            type="headerMobileMenu"
                            onClick={openSideMenu}
                            className={`${style.menu} cms-icon-side-menu`}
                            aria-label={translate('header.sideMenu.aria-label')}
                        />
                    )}
                    {isShowBackButton && (
                        <Icon
                            type="backButton"
                            onClick={handleBackButtonClick}
                            className="cms-icon-back-button"
                            aria-label={translate('header.backBtn')}
                        />
                    )}
                </span>
                <Logo />
                <Suspense fallback="">
                    <TotalizatorSessionTicker className={clsx([style.ticker, !isLoggedIn && style.tickerGuest])} />
                </Suspense>
            </div>
            <div className={style.controls}>
                {isLoggedIn
                    ? (
                        <>
                            <MyBalance />
                            <div className={style.deposit}>
                                <BackboneReplacer dynamicName="HEADER_DEPOSIT_BUTTON" viewName="headerdepositbutton" />
                            </div>
                            <Inbox />
                            <MyAccount />
                            {Chat}
                            <DropdownAccount />
                        </>
                    )
                    : (
                        <>
                            {Chat}
                            <Button
                                size="small"
                                color="success"
                                view="secondary"
                                className={`${style.login} ${style.btn}`}
                                onClick={onLoginButtonClick}
                            >
                                {translate('login.button.loginHeader')}
                            </Button>
                            {registrationURL && (
                                <Button
                                    size="small"
                                    color="revert"
                                    view="secondary"
                                    href={registrationURL}
                                    className={`${style.register} ${style.btn}`}
                                >
                                    {translate('login.button.join_now')}
                                </Button>
                            )}
                        </>
                    )
                }
            </div>
        </div>
    );
};
